import useCostCategories from '@/hooks/useCostCategories'
import { thermostatsCollection } from '@/reducers/thermostats'
import { Thermostat, ThermostatSettings } from '@/types'
import { unenrollmentThermostatDisplayName } from '../../../utils'
import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { thermostatComfortSettingsCollection } from '@/reducers/thermostatComfortSettings'
import { useThermostatInactivityPeriodsCollection } from '@/reducers/thermostatInactivityPeriods'
import { inactiveComfortSettingsCollection } from '@/reducers/inactiveComfortSettings'
import { getInactivePeriodSummarySubtitleLines } from '@/app/pages/thermostat/cost-categories/utils/inactivityPeriodFormatting'
import { getGridSupportSettingsSummary } from './ThermostatSuggestionCard.utils'
import { SuggestionCardProps } from '../DeviceSuggestionCard'
import { useDataAndSetLoadedState } from '../../hooks/useDataAndSetLoadedState'

export type Props = SuggestionCardProps & {
  thermostatId: Thermostat['id']
  settingsWhitelist: string[]
}

export function useLoadData(props: Props) {
  const { data: thermostats, isLoadingOrIdle: thermostatsLoadingOrIdle } =
    thermostatsCollection.useFetch()
  const thermostat = thermostats.find(
    (thermostat) => thermostat.id === props.thermostatId,
  )

  const { costCategories, isLoading: costCategoryControlsLoading } =
    useCostCategories(props.thermostatId)

  const { data: settingsArray, isLoadingOrIdle: settingsLoadingOrIdle } =
    thermostatSettingsCollection.useFetch(
      `/thermostats/${props.thermostatId}/settings/`,
    )
  const settings = settingsArray[0] as ThermostatSettings | undefined

  const {
    data: comfortSettings,
    isLoadingOrIdle: comfortSettingsLoadingOrIdle,
  } = thermostatComfortSettingsCollection.useFetch(
    `/thermostats/${props.thermostatId}/comfort_settings/`,
  )

  const firstComfortSettingId = comfortSettings[0]?.id
  const {
    data: inactiveSchedules,
    isLoadingOrIdle: inactiveSchedulesLoadingOrIdle,
  } = inactiveComfortSettingsCollection.useFetch(
    `/thermostats/${props.thermostatId}/comfort_settings/${
      firstComfortSettingId ?? ''
    }/inactive/`,
    { require: Boolean(firstComfortSettingId) },
  )

  const {
    data: inactivityPeriods,
    isLoadingOrIdle: inactivityPeriodsLoadingOrIdle,
  } = useThermostatInactivityPeriodsCollection(props.thermostatId)

  const isLoadingOrIdle =
    thermostatsLoadingOrIdle ||
    costCategoryControlsLoading ||
    settingsLoadingOrIdle ||
    comfortSettingsLoadingOrIdle ||
    inactiveSchedulesLoadingOrIdle ||
    inactivityPeriodsLoadingOrIdle

  return {
    data: {
      thermostat,
      costCategories,
      settings,
      comfortSettings,
      inactivityPeriods,
      inactiveSchedules,
    },
    isLoadingOrIdle,
  }
}

export default function useViewModel(props: Props) {
  const { articles, settingsWhitelist } = props
  const { data, isLoadingOrIdle } = useDataAndSetLoadedState({
    useLoader: useLoadData,
    props,
    setLoadedSignal: props.setLoadedSignal,
  })

  const displayName = data.thermostat
    ? unenrollmentThermostatDisplayName(data.thermostat)
    : ''

  const inactiveTimesCardSubtitleLines = data.thermostat
    ? getInactivePeriodSummarySubtitleLines({
        thermostat: data.thermostat,
        inactivityPeriods: data.inactivityPeriods,
        comfortSettings: data.comfortSettings,
        inactiveComfortSettings: data.inactiveSchedules,
      })
    : []
  const settingPaths = {
    costCategories: `/devices/thermostat/${props.thermostatId}/cost-categories`,
    gridSupport: `/devices/thermostat/${props.thermostatId}/grid-support`,
    precool: `/devices/thermostat/${props.thermostatId}/precool-preheat-settings`,
  }

  const gridSupportSettingsSummary = data.thermostat
    ? getGridSupportSettingsSummary(data.comfortSettings, data.thermostat)
    : []

  return {
    displayName: displayName,
    isLoadingOrIdle,
    settingPaths,
    costCategories: data.costCategories,
    inactiveTimesCardSubtitleLines,
    gridSupportEnabled: data.settings?.grid_support_enabled ?? false,
    gridSupportSettingsSummary,
    precoolEnabled: data.settings?.pre_cool_enabled ?? false,
    articles,
    settingsWhitelist,
  }
}
